import type { DataSourceDef } from '../graph/dataGraph.js';
import { resolveDataField } from '../graph/dataSourceSchema.js';
import { createMutationEntityId } from '../ids/entityIds.js';
import type { AppState } from '../state/appState.js';
import { normalizeDataSourceDocument, projectDataRecords } from './templateLoader.js';
import type { DataRuntimeManager } from './dataRuntime.js';
import { DataSourceStoreHandle } from 'fluveria-engine-wasm';
import {
  type CredentialResolver,
  type DataSourceAccess,
  type DataMutationOperation,
  DataMutationRejectedError,
  spliceText,
  type DataMutationResult,
  type DataProviderRegistry,
  type DataSourceDelta,
  type DataSourceSession,
  type DataSourceStatus,
} from './dataProviders.js';

export interface DataSourceRuntimeStatus {
  status: DataSourceStatus;
  revision: number;
  schemaRevision: number;
  access?: DataSourceAccess;
  error?: string;
}

interface ActiveSession {
  generation: number;
  abort: AbortController;
  session: DataSourceSession;
  unsubscribe?: () => void;
  unsubscribeFailure?: () => void;
}

interface RevisionWaiter {
  generation: number;
  revision: number;
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

/**
 * A local mutation the reader already sees and the source has not yet
 * delivered back, as the engine's store gives it (`PendingMutation`).
 */
export interface PendingMutation {
  mutationId: string;
  /** In the shape of the records read: field ids, projected records. */
  operations: DataMutationOperation[];
  /**
   * The revision the source accepted it at, once it has. It stays pending
   * until the confirmed records reach that revision, or until a delta or a
   * snapshot names it as one it carries.
   */
  acceptedAt?: number;
}

const MUTATION_CONFIRM_TIMEOUT_MS = 10_000;

/**
 * Opens a document's data sources and keeps what it reads of each in the
 * engine's `DataSourceStore` (through `DataSourceStoreHandle`): the confirmed
 * records with the local mutations not yet confirmed laid over them, replayed
 * in the order they were made. The pending layer -- laying a mutation over,
 * settling it by the mutations a delta or a snapshot names or by the revision
 * it was accepted at, taking a refused one back out, and the flicker rule (a
 * delta for a field a pending mutation covers does not show until that
 * mutation is confirmed), and bringing a pending splice forward over another
 * writer's splice a delta carries (`applied`, kb:task/276) so a text two
 * people type in does not jump -- is the engine's, not a twin of it here. The
 * query store reads the same store, so a query sees what the reader sees.
 *
 * Edits are optimistic and sent as they are made (kb:decision/53): the source
 * applies them in the order they arrive, and refuses one only on its meaning.
 * A refusal takes that mutation back out; it is never news that this reader
 * is behind, so it neither turns the source stale nor reads it again.
 */
export class DataSourceRuntimeManager {
  private readonly store = new DataSourceStoreHandle();
  private readonly sessions = new Map<string, ActiveSession>();
  private readonly statuses = new Map<string, DataSourceRuntimeStatus>();
  /** The records last handed to the reader: the store's view of each source. */
  private readonly views = new Map<string, unknown>();
  private generation = 0;
  private readonly listeners = new Set<() => void>();
  private readonly revisionWaiters = new Map<string, Set<RevisionWaiter>>();
  private credentialResolver: CredentialResolver;

  constructor(
    private readonly appState: AppState,
    private readonly dataRuntime: DataRuntimeManager,
    private readonly registry: DataProviderRegistry,
    resolveCredential: CredentialResolver = async () => null,
    private readonly baseUrl?: string,
    private readonly resourceUrls: Readonly<Record<string, string>> = {},
  ) { this.credentialResolver = resolveCredential; }

  setCredentialResolver(resolver: CredentialResolver): void { this.credentialResolver = resolver; }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  status(sourceId: string): DataSourceRuntimeStatus {
    return this.statuses.get(sourceId) ?? { status: 'idle', revision: 0, schemaRevision: 0 };
  }

  storeHandle(): DataSourceStoreHandle { return this.store; }

  /** The mutations of a source the reader sees and the source has not confirmed, oldest first. */
  pendingMutations(sourceId: string): readonly Readonly<PendingMutation>[] {
    return this.store.pending(sourceId) as PendingMutation[];
  }

  /**
   * Validate a mutation against what the reader sees, lay it over the records
   * at once, and send it now -- not after the mutations before it are
   * answered; the source takes them in the order they were sent. It stays
   * laid over until the confirmed records reach the revision the source
   * accepted it at, or a delta names it; a mutation the source refuses, or
   * that never reaches it, is taken back out as if it had never been made.
   */
  mutate(sourceId: string, operations: readonly DataMutationOperation[]): Promise<DataMutationResult> {
    let cloned: DataMutationOperation[];
    try {
      cloned = structuredClone(operations) as DataMutationOperation[];
    } catch (error) {
      return Promise.reject(new Error(`Data source '${sourceId}' mutation is not serializable: ${error instanceof Error ? error.message : String(error)}`));
    }
    let active: ActiveSession;
    let mutationId: string;
    try {
      const source = this.appState.dataSources[sourceId];
      if (!source) throw new Error(`Unknown data source '${sourceId}'`);
      const connected = this.sessions.get(sourceId);
      if (!connected) throw new Error(`Data source '${sourceId}' is not connected`);
      if (!connected.session.mutate) throw new Error(`Data source '${sourceId}' is read-only`);
      const status = this.status(sourceId);
      if (status.status !== 'ready') throw new Error(`Data source '${sourceId}' is not ready for mutation (${status.status})`);
      validateSessionAccess(sourceId, status.access, cloned);
      validateMutationOperations(sourceId, source, this.views.get(sourceId), cloned);
      active = connected;
      mutationId = createMutationEntityId();
      this.pushPending(sourceId, source, mutationId, cloned);
    } catch (error) {
      return Promise.reject(error);
    }
    return this.send(sourceId, active, mutationId, cloned);
  }

  async connectAll(): Promise<void> {
    const declared = new Set(Object.keys(this.appState.dataSources));
    for (const sourceId of this.sessions.keys()) if (!declared.has(sourceId)) this.close(sourceId);
    const results = await Promise.allSettled(Object.entries(this.appState.dataSources).map(([id, source]) => this.connect(id, source)));
    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failure) throw failure.reason;
  }

  async connect(sourceId: string, source: DataSourceDef): Promise<void> {
    this.close(sourceId);
    const generation = ++this.generation;
    const abort = new AbortController();
    this.statuses.set(sourceId, { status: 'loading', revision: 0, schemaRevision: 0 });
    try {
      const resolvedSource = { ...source, resource: this.resourceUrls[source.resource] ?? source.resource };
      const session = await this.registry.get(source.provider).connect(resolvedSource, {
        sourceId,
        baseUrl: this.baseUrl,
        signal: abort.signal,
        resolveCredential: this.credentialResolver,
      });
      if (abort.signal.aborted) { session.close(); return; }
      const active: ActiveSession = { generation, abort, session };
      this.sessions.set(sourceId, active);
      active.unsubscribeFailure = session.onFailure?.(error => this.acceptFailure(sourceId, generation, error));
      await this.acceptSnapshot(sourceId, source, generation, await session.snapshot());
      active.unsubscribe = session.subscribe?.(delta => this.acceptDelta(sourceId, source, generation, delta));
    } catch (error) {
      if (abort.signal.aborted) return;
      const active = this.sessions.get(sourceId);
      if (active?.generation === generation) {
        active.unsubscribeFailure?.();
        active.session.close();
        this.sessions.delete(sourceId);
      }
      this.statuses.set(sourceId, {
        status: 'error', revision: 0, schemaRevision: 0,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  async refresh(sourceId: string): Promise<void> {
    const active = this.sessions.get(sourceId);
    const source = this.appState.dataSources[sourceId];
    if (!active || !source) return this.connect(sourceId, source);
    if (!active.session.refresh) return this.connect(sourceId, source);
    this.statuses.set(sourceId, { ...this.status(sourceId), status: 'loading' });
    await this.acceptSnapshot(sourceId, source, active.generation, await active.session.refresh());
  }

  close(sourceId: string): void {
    this.rejectRevisionWaiters(sourceId, new Error(`Data source '${sourceId}' closed before confirming its mutation`));
    const active = this.sessions.get(sourceId);
    if (active) {
      active.unsubscribeFailure?.();
      active.abort.abort();
      active.unsubscribe?.();
      active.session.close();
      this.sessions.delete(sourceId);
    }
    if (this.statuses.has(sourceId)) this.statuses.set(sourceId, { ...this.status(sourceId), status: 'closed' });
  }

  closeAll(): void {
    for (const sourceId of [...this.sessions.keys()]) this.close(sourceId);
  }

  /**
   * Take a snapshot as the source's confirmed records. A refresh is not an
   * acknowledgement: the pending mutations stay laid over it, except those it
   * names and those its revision already includes.
   */
  private async acceptSnapshot(
    sourceId: string,
    source: DataSourceDef,
    generation: number,
    snapshot: { revision: number; schemaRevision: number; records: unknown; document?: unknown; mutationIds?: readonly string[] },
  ): Promise<void> {
    if (this.sessions.get(sourceId)?.generation !== generation) return;
    const document = snapshot.document ?? snapshot.records;
    const records = normalizeDataSourceDocument(document, source, sourceId);
    const access = this.sessions.get(sourceId)?.session.access?.() ?? undefined;
    this.store.replaceSnapshot({
      sourceId, revision: snapshot.revision, schemaRevision: snapshot.schemaRevision, records,
      ...(snapshot.mutationIds ? { mutationIds: [...snapshot.mutationIds] } : {}),
    });
    this.statuses.set(sourceId, {
      status: 'ready', revision: snapshot.revision, schemaRevision: snapshot.schemaRevision,
      ...(access ? { access } : {}),
    });
    this.publish(sourceId, { document });
    this.resolveRevisionWaiters(sourceId);
    this.listeners.forEach(listener => listener());
  }

  private acceptFailure(sourceId: string, generation: number, error: Error): void {
    if (this.sessions.get(sourceId)?.generation !== generation) return;
    this.rejectRevisionWaiters(sourceId, error);
    this.statuses.set(sourceId, { ...this.status(sourceId), status: 'error', error: error.message });
    this.listeners.forEach(listener => listener());
  }

  /**
   * Apply a delta to the confirmed records; the pending mutations it names
   * stop being pending with it, acknowledged or not. A delta the confirmed
   * records already include -- one a snapshot overtook -- writes nothing and
   * only lets go of what it names; one that starts anywhere else is a revision
   * gap, and a refresh catches it up.
   */
  private acceptDelta(sourceId: string, source: DataSourceDef, generation: number, delta: DataSourceDelta): void {
    if (this.sessions.get(sourceId)?.generation !== generation) return;
    const status = this.status(sourceId);
    let viewChanged = false;
    const { settled } = this.settling(sourceId, () => {
      viewChanged = this.store.applyDelta({
        sourceId,
        baseRevision: delta.baseRevision,
        revision: delta.revision,
        upserts: projectUpserts(source, delta.upserts ?? {}),
        removes: delta.removes ?? [],
        changedFields: delta.changedFields ?? [],
        mutationIds: delta.mutationIds ?? [],
        applied: delta.applied ?? [],
      });
    }, () => {
      this.statuses.set(sourceId, { ...status, status: 'stale', error: 'Source revision mismatch; refresh required' });
      void this.refresh(sourceId);
    });
    if (settled === null) return;
    const revision = (this.store.status(sourceId) as { revision: number } | null)?.revision ?? status.revision;
    // A delta a snapshot overtook writes nothing: only the mutations it let
    // go of can have changed what the reader sees.
    const advanced = revision > status.revision;
    if (advanced) this.statuses.set(sourceId, { ...status, status: 'ready', revision, error: undefined });
    if (viewChanged) {
      const written = advanced ? (delta.changedFields?.length ? delta.changedFields : ['*']) : [];
      this.publish(sourceId, { changedFields: [...written, ...settled] });
    }
    if (advanced) this.resolveRevisionWaiters(sourceId);
    if (advanced || viewChanged) this.listeners.forEach(listener => listener());
  }

  /**
   * Send a mutation already laid over the records. The session is asked at
   * once, so mutations reach the source in the order they were made.
   * `baseRevision` is the confirmed revision the mutation was made against:
   * the source does not check it.
   */
  private async send(
    sourceId: string,
    active: ActiveSession,
    mutationId: string,
    operations: DataMutationOperation[],
  ): Promise<DataMutationResult> {
    const { generation } = active;
    const baseRevision = this.status(sourceId).revision;
    let result: DataMutationResult;
    try {
      result = await active.session.mutate!({ mutationId, baseRevision, operations });
      if (this.sessions.get(sourceId)?.generation !== generation) {
        throw new Error(`Data source '${sourceId}' changed session before confirming mutation '${mutationId}'`);
      }
      if (result.mutationId !== mutationId) {
        throw new Error(`Data source '${sourceId}' returned the wrong mutation id`);
      }
      if (!Number.isSafeInteger(result.revision) || result.revision <= baseRevision) {
        throw new Error(`Data source '${sourceId}' mutation '${mutationId}' returned an invalid revision`);
      }
    } catch (error) {
      this.takeBack(sourceId, mutationId);
      throw error;
    }
    this.acknowledge(sourceId, mutationId, result.revision);
    await this.waitForRevision(sourceId, generation, result.revision);
    return result;
  }

  /** Lay a local mutation over the source's records at once, ahead of the source's answer. */
  private pushPending(sourceId: string, source: DataSourceDef, mutationId: string, operations: readonly DataMutationOperation[]): void {
    const projected = operations.map(operation => operation.kind === 'upsert'
      ? { ...operation, record: (projectDataRecords([operation.record], source) as unknown[])[0] }
      : operation);
    if (this.store.pushPending(sourceId, mutationId, projected)) {
      this.publish(sourceId, { changedFields: operationFields(projected) });
    }
  }

  /**
   * The source accepted a mutation at `revision`. It stops being pending once
   * the confirmed records reach that revision -- now, if they have. One a
   * delta already named is gone, and this changes nothing.
   */
  private acknowledge(sourceId: string, mutationId: string, revision: number): void {
    let viewChanged = false;
    const { settled } = this.settling(sourceId, () => {
      viewChanged = this.store.acknowledge(sourceId, mutationId, revision);
    });
    if (viewChanged) this.publish(sourceId, { changedFields: settled ?? ['*'] });
  }

  /**
   * Take a mutation the source refused, or that never reached it, back out
   * of the view, as if it had never been made.
   */
  private takeBack(sourceId: string, mutationId: string): void {
    const taken = this.pendingMutations(sourceId).find(mutation => mutation.mutationId === mutationId);
    if (taken && this.store.reject(sourceId, mutationId)) {
      this.publish(sourceId, { changedFields: operationFields(taken.operations) });
    }
  }

  /**
   * Run a change of the store and say which fields the pending mutations it
   * let go of wrote -- `null` when the change threw, after `failed` has run.
   */
  private settling(sourceId: string, change: () => void, failed?: () => void): { settled: string[] | null } {
    const before = this.pendingMutations(sourceId);
    try {
      change();
    } catch (error) {
      if (!failed) throw error;
      failed();
      return { settled: null };
    }
    const kept = new Set(this.pendingMutations(sourceId).map(mutation => mutation.mutationId));
    return {
      settled: operationFields(before.filter(mutation => !kept.has(mutation.mutationId)).flatMap(mutation => mutation.operations)),
    };
  }

  /**
   * Hand the reader the source's records as they now read: the store's view,
   * which the query store is too.
   */
  private publish(sourceId: string, change: { document?: unknown; changedFields?: readonly string[] }): void {
    const view = this.store.records(sourceId);
    this.views.set(sourceId, view);
    if (change.document !== undefined) {
      this.dataRuntime.setDataSource(sourceId, view, change.document);
    } else {
      this.dataRuntime.setDataSource(sourceId, view, undefined, change.changedFields?.length ? change.changedFields : ['*']);
    }
  }

  private waitForRevision(sourceId: string, generation: number, revision: number): Promise<void> {
    const status = this.status(sourceId);
    if (this.sessions.get(sourceId)?.generation === generation && status.revision >= revision) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const waiters = this.revisionWaiters.get(sourceId) ?? new Set<RevisionWaiter>();
      const waiter: RevisionWaiter = {
        generation,
        revision,
        resolve: () => {
          clearTimeout(waiter.timeout);
          waiters.delete(waiter);
          if (!waiters.size) this.revisionWaiters.delete(sourceId);
          resolve();
        },
        reject: error => {
          clearTimeout(waiter.timeout);
          waiters.delete(waiter);
          if (!waiters.size) this.revisionWaiters.delete(sourceId);
          reject(error);
        },
        timeout: setTimeout(() => {
          const current = this.status(sourceId);
          this.statuses.set(sourceId, {
            ...current,
            status: 'stale',
            error: `Mutation confirmation timed out waiting for revision ${revision}`,
          });
          waiter.reject(new Error(`Data source '${sourceId}' did not reach mutation revision ${revision}`));
        }, MUTATION_CONFIRM_TIMEOUT_MS),
      };
      waiters.add(waiter);
      this.revisionWaiters.set(sourceId, waiters);
      this.resolveRevisionWaiters(sourceId);
    });
  }

  private resolveRevisionWaiters(sourceId: string): void {
    const active = this.sessions.get(sourceId);
    const status = this.status(sourceId);
    for (const waiter of [...(this.revisionWaiters.get(sourceId) ?? [])]) {
      if (active?.generation === waiter.generation && status.revision >= waiter.revision) waiter.resolve();
    }
  }

  private rejectRevisionWaiters(sourceId: string, error: Error): void {
    for (const waiter of [...(this.revisionWaiters.get(sourceId) ?? [])]) waiter.reject(error);
  }
}

function validateSessionAccess(
  sourceId: string,
  access: DataSourceAccess | undefined,
  operations: readonly DataMutationOperation[],
): void {
  if (!access) return;
  for (const operation of operations) {
    if (!access.permissions.operations.includes(operation.kind)) {
      throw new DataMutationRejectedError(
        `Data source '${sourceId}' does not allow '${operation.kind}' mutations for the current user`,
        { code: 'forbidden' },
      );
    }
    const key = String(operation.key);
    const matchingGrant = access.permissions.grants.some(grant => (
      grant.operations.includes(operation.kind)
      && (!grant.records || grant.records.includes(key))
      && ((operation.kind !== 'setField' && operation.kind !== 'spliceField')
        || !grant.fields || grant.fields.includes(operation.field))
    ));
    if (!matchingGrant) {
      throw new DataMutationRejectedError(
        `Data source '${sourceId}' does not allow this '${operation.kind}' mutation for the current user`,
        { code: 'forbidden' },
      );
    }
  }
}

function validateMutationOperations(
  sourceId: string,
  source: DataSourceDef,
  records: unknown,
  operations: readonly DataMutationOperation[],
): void {
  if (!operations.length) throw new Error(`Data source '${sourceId}' mutation has no operations`);
  if (!records || typeof records !== 'object') throw new Error(`Data source '${sourceId}' has no loaded records`);
  for (const operation of operations) {
    validateMutationKey(sourceId, source, records, operation);
    if (operation.kind === 'setField') {
      const field = source.fields[operation.field];
      if (!field) throw new Error(`Data source '${sourceId}' has no field '${operation.field}'`);
      if (!matchesMutationValue(operation.value, field.type)) {
        throw new Error(`Data source '${sourceId}' field '${operation.field}' mutation value is not a ${field.type}`);
      }
    } else if (operation.kind === 'upsert') {
      if (!operation.record || typeof operation.record !== 'object' || Array.isArray(operation.record)) {
        throw new Error(`Data source '${sourceId}' upsert record must be an object`);
      }
      for (const [fieldId, field] of Object.entries(source.fields)) {
        const value = resolveDataField(operation.record, field.path);
        if (value === undefined) throw new Error(`Data source '${sourceId}' upsert record is missing field '${fieldId}'`);
        if (!matchesMutationValue(value, field.type)) {
          throw new Error(`Data source '${sourceId}' upsert field '${fieldId}' is not a ${field.type}`);
        }
      }
    } else if (operation.kind === 'spliceField') {
      validateSplice(sourceId, source, records, operation);
    }
  }
}

/**
 * A splice is checked against the text the reader sees, as the source checks
 * it against the text it holds: a field that is not there yet is an empty
 * text, and one that is no text or that the splice runs past is refused as
 * `schema`.
 */
function validateSplice(
  sourceId: string,
  source: DataSourceDef,
  records: object,
  operation: Extract<DataMutationOperation, { kind: 'spliceField' }>,
): void {
  const field = source.fields[operation.field];
  if (!field) throw new Error(`Data source '${sourceId}' has no field '${operation.field}'`);
  const count = (value: unknown): boolean => Number.isSafeInteger(value) && (value as number) >= 0;
  if (!count(operation.at) || !count(operation.delete) || typeof operation.insert !== 'string') {
    throw new Error(`Data source '${sourceId}' splice needs whole positions and a text to insert`);
  }
  const record = (records as Record<string, unknown>)[String(operation.key)] as Record<string, unknown> | undefined;
  const current = record?.[operation.field] ?? '';
  if (field.type !== 'string' || typeof current !== 'string') {
    throw new DataMutationRejectedError(
      `Data source '${sourceId}' field '${operation.field}' of record '${String(operation.key)}' is not a text to splice`,
      { code: 'schema' },
    );
  }
  if (!spliceText(current, operation.at, operation.delete, operation.insert)) {
    throw new DataMutationRejectedError(
      `Data source '${sourceId}' splice ${operation.at}+${operation.delete} runs past the ${Array.from(current).length} characters of field '${operation.field}' of record '${String(operation.key)}'`,
      { code: 'schema' },
    );
  }
}

/** The fields a reader may see change by these operations; a whole record is every field. */
function operationFields(operations: readonly DataMutationOperation[]): string[] {
  return [...new Set(operations.map(operation => (
    operation.kind === 'setField' || operation.kind === 'spliceField' ? operation.field : '*'
  )))];
}

function validateMutationKey(
  sourceId: string,
  source: DataSourceDef,
  records: object,
  operation: DataMutationOperation,
): void {
  if (typeof operation.key !== source.keyType) {
    throw new Error(`Data source '${sourceId}' mutation key must be a ${source.keyType}`);
  }
  if (typeof operation.key === 'number' && !Number.isFinite(operation.key)) {
    throw new Error(`Data source '${sourceId}' mutation key must be finite`);
  }
  if (source.records.kind === 'array') {
    if (typeof operation.key !== 'number' || !Number.isInteger(operation.key) || operation.key < 0) {
      throw new Error(`Data source '${sourceId}' array mutation key must be a non-negative integer`);
    }
    const length = (records as unknown[]).length;
    if (operation.kind === 'upsert') {
      if (operation.key > length) throw new Error(`Data source '${sourceId}' array mutation key leaves a gap`);
    } else if (operation.key >= length) {
      throw new Error(`Data source '${sourceId}' has no record at key ${operation.key}`);
    }
    return;
  }
  const key = String(operation.key);
  if (operation.kind !== 'upsert' && !Object.hasOwn(records, key)) {
    throw new Error(`Data source '${sourceId}' has no record at key '${key}'`);
  }
}

function matchesMutationValue(value: unknown, type: DataSourceDef['fields'][string]['type']): boolean {
  if (value === null) return true;
  if (type === 'bool') return typeof value === 'boolean';
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'string') return typeof value === 'string';
  const components = Number(type.slice(3));
  return Array.isArray(value)
    && value.length === components
    && value.every(component => typeof component === 'number' && Number.isFinite(component));
}

/** A delta's records in the shape a reader reads: field ids, projected as a snapshot's are. */
function projectUpserts(source: DataSourceDef, upserts: Record<string, unknown>): Record<string, unknown> {
  const keys = Object.keys(upserts);
  const projected = projectDataRecords(Object.values(upserts), source) as unknown[];
  return Object.fromEntries(keys.map((key, index) => [key, projected[index]]));
}
